// Detects whether the ApplePaySession API is usable, for the Tap Apple Pay
// button. Safari provides it natively; on other desktop browsers Apple's
// official JS SDK provides the same API backed by the web-based flow
// introduced with iOS 18 (click -> QR code -> finish on iPhone), so the probe
// loads that SDK first. The Tap SDK drives ApplePaySession directly and loads
// the same Apple SDK itself, so its button works wherever this reports true.

declare global {
  interface Window {
    ApplePaySession?: {
      canMakePayments: () => boolean;
    };
  }
}

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

const canMakePayments = (): boolean => {
  try {
    return !!window.ApplePaySession?.canMakePayments();
  } catch (error) {
    console.error('Apple Pay support check failed:', error);
    return false;
  }
};

// The probe's script tag MUST be removed once settled: the Tap SDK's loader
// reuses any existing tag with this src and reads readiness from its
// data-status attribute - a foreign tag without it deadlocks Tap's init and
// the rendered button silently ignores clicks. With the tag gone, Tap creates
// its own properly-attributed tag; Apple's script guards re-execution and the
// ApplePaySession global survives tag removal.
const loadApplePaySdk = () =>
  new Promise<void>((resolve) => {
    const script = document.createElement('script');
    const settle = () => {
      script.remove();
      resolve();
    };
    script.src = APPLE_PAY_SDK_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = settle;
    script.onerror = settle;
    document.head.appendChild(script);
  });

// The SDK defines ApplePaySession synchronously on load today (verified), but
// the URL floats on 1.latest - a brief bounded wait keeps a future
// async-defining build from silently reporting false.
const waitForApplePaySession = async () => {
  for (let attempt = 0; attempt < 5 && !window.ApplePaySession; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
};

export const checkApplePaySupport = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (!window.ApplePaySession) {
      await loadApplePaySdk();
      await waitForApplePaySession();
    }
  } catch (error) {
    console.error('Apple Pay support check failed:', error);
    return false;
  }
  return canMakePayments();
};
